import React, { useState, useEffect } from 'react';
import { Loading } from '../../components/common/Loading';
import SiteCard from '../../components/site/SiteCard';
import ProductCard from '../../components/product/ProductCard';
import { ProductAction, SiteAction } from '../../actions';
import ServiceCreateForm from './ServiceCreateForm';
import { isDOA, isDIM, Flag } from '../../components/common/CommonViews';
import { Translate as t } from '../../config/Translate';

// 검색 단계 + 제품/사이트 요약 카드. 입력 양식은 ServiceCreateForm 이 그린다.
const ServiceCreate = ({ callbackCreated, sn = "", siteId = "", withBack = false }) => {
	const [searchView, setSearchView] = useState(true);
	const [search, setSearch] = useState("");
	const [product, setProduct] = useState([]);
	const [searched, setSearched] = useState(false);
	const [sites, setSites] = useState([]);
	const [searchLoading, setSearchLoading] = useState(false);
	const [pInfo, setpInfo] = useState({ sn: "" });
	const [sInfo, setSInfo] = useState({ siteId: "" });
	// sn·siteId 가 쿼리로 올 때 제품·사이트를 읽는 동안 검색 화면 대신 표시
	const [initLoading, setInitLoading] = useState(!!(sn || siteId));

	useEffect(() => {
		let alive = true;
		let job = null;
		if (sn) {
			job = getProduct(sn);
		} else if (siteId) {
			// 사이트만 들고 들어온 경우: 사이트를 읽어 검색에서 고른 것과 같은 규칙으로 진행
			job = openSite(siteId);
		}
		Promise.resolve(job).finally(() => {
			if (alive) setInitLoading(false);
		});
		return () => { alive = false; };
	}, []);

	useEffect(() => {
		if (pInfo?.sn) {
			setSearchView(false);
		}
	}, [pInfo]);

	const openSite = async (sId) => {
		try {
			const ret = await SiteAction.GetSiteLocal(sId);
			if (ret?.siteId) {
				await selectSite(ret);
			} else {
				console.warn('[ServiceCreate] openSite: site not found', { siteId: sId });
			}
		} catch (error) {
			console.warn('[ServiceCreate] openSite failed:', error?.message);
		}
	}

	const getSite = async (sId) => {
		try {
			const ret = await SiteAction.GetSiteLocal(sId);
			setSInfo(ret || { siteId: "" });
		} catch (error) {
			console.warn('[ServiceCreate] getSite failed:', error?.message);
		}
	}

	const getProduct = async (sn) => {
		try {
			const ret = await ProductAction.GetProductLocal(sn);
			if (ret?.siteId) {
				getSite(ret.siteId);
			}
			setpInfo(ret || { sn: "" });
		} catch (error) {
			console.warn('[ServiceCreate] getProduct failed:', error?.message);
		}
	}

	const searchProduct = async () => {
		const filter = {};
		if (search) {
			filter['match'] = { sn: { $regex: search.trim().toUpperCase(), $options: 'si' } };
		}

		if (Object.keys(filter).length > 0) {
			const ret = await ProductAction.GetProductDirect(filter);
			setProduct(ret.list || []);
		}
	}

	const searchSite = async () => {
		const ret = await SiteAction.GetSearchSiteAll(['sitename', 'customerFirstName', 'customerLastName'], search.trim());
		if (ret.data.status == 'success') {
			setSites(ret.data.data.list);
		} else {
			setSites([]);
		}
	}

	const searchGo = async (e) => {
		e.preventDefault();
		if (search.length >= 3) {
			setSearchLoading(true);
			setSearched(true);
			await Promise.all([searchProduct(), searchSite()]);
			setSearchLoading(false);
		}
	}

	const setPrd = (prd) => {
		getProduct(prd?.sn);
	}

	const selectSite = async (site) => {
		try {
			const ret = await ProductAction.GetProductDirect({ match: { siteId: site.siteId } });
			const list = ret?.list || [];
			if (list.length === 1) {
				await getProduct(list[0].sn);
			} else if (list.length > 1) {
				setSites([]);
				setProduct(list);
			} else {
				setSInfo(site);
				setpInfo({ sn: "" });
				setSearchView(false);
			}
		} catch (error) {
			console.warn('[ServiceCreate] selectSite failed:', error?.message);
		}
	}

	const updateSearchKey = (val) => {
		setSearch(val);
		setSearched(false);
	}

	const isOnline = () => {
		return pInfo.isConnected ?
			<ion-icon name="wifi" className="text-primary md hydrated" style={{ pointerEvents: 'none' }}></ion-icon> :
			<ion-icon name="wifi-outline" className="text-muted md hydrated" style={{ pointerEvents: 'none' }} ></ion-icon>;
	}

	if (initLoading) return <div className="mt-3"><Loading /></div>;

	return (
		<>
			{searchView && <>
				<div className="sch p-2 bg-white mb-1">
					<form className="search-form" onSubmit={(e) => searchGo(e)}>
						<div className="form-group searchbox">
							<input className="form-control fs-22"
								type="text"
								onChange={(e) => updateSearchKey(e.target.value)}
								placeholder={t('product.field.snOrSiteName')}
								value={search} />
							<i className="input-icon">
								<ion-icon name="search-outline" role="img" className="md hydrated" aria-label="search outline"></ion-icon>
							</i>
						</div>
						{search.length < 3 && <button className="btn btn-primary btn-block disabled" onClick={(e) => searchGo(e)}>{t('common.button.search')}</button>}
						{search.length >= 3 && <button className="btn btn-primary btn-block" onClick={(e) => searchGo(e)}>{t('common.button.search')}</button>}
					</form>
				</div>
				{product?.length > 0 &&
					<div className="section mb-2">
						<div className="card mt-2">
							<div className="card-header">
								<h4 className="mb-0">
									{t('product.title')}
									<span className="small"> ({product.length.toLocaleString()})</span>
								</h4>
							</div>
							<ul className="listview link-listview">
								{product.map(x => <li key={x.sn} onClick={() => setPrd(x)}>
									<ProductCard item={x} showOnly={true} />
								</li>)}
							</ul>
						</div>
					</div>
				}
				{sites.length > 0 &&
					<div className="section mb-2">
						<div className="card">
							<div className="card-header">
								<h4 className="mb-0">
									{t('site.title')}
									<span className="small"> ({sites.length.toLocaleString()})</span>
								</h4>
							</div>
							<ul className="listview link-listview">
								{sites.map(x => <li key={x.siteId} className="border-bottom" onClick={() => selectSite(x)}>
									<SiteCard item={x} showOnly={true} />
								</li>)}
							</ul>
						</div>
					</div>
				}
				{(!product?.length && !sites?.length && searched && search) &&
					<div className="section mb-2 mt-1">
						<div className="card">
							<div className="card-body text-center">
								{searchLoading ? t('common.message.pleaseWait') : t('common.message.noRecord')}
							</div>
						</div>
					</div>
				}
			</>}
			{!searchView && <>
				<div className="card me-1 ms-1 mt-1">
					<div className="item p-2">
						<div className='in'>
							<strong>{isOnline()} {pInfo.sn} {isDOA(pInfo)} {isDIM(pInfo)} </strong>
							<div>
								<strong>{Flag(sInfo.country)}{sInfo.sitename} </strong>
							</div>
							<div className="text-muted">{pInfo.model} {pInfo.option && "-" + pInfo.option}</div>
						</div>
					</div>
				</div>
				<ServiceCreateForm
					prefill={pInfo?.sn ? { sn: pInfo.sn, model: pInfo.model, siteId: pInfo.siteId } : { siteId: sInfo?.siteId }}
					product={pInfo}
					callbackCreated={callbackCreated}
					withBack={withBack}
					onCancel={() => setSearchView(true)} />
			</>}
		</>
	);
};

export default ServiceCreate;
